import {
  PayrollAnchorField,
  PayrollFileColumnMapping,
  PayrollFileColumnTransformationColumn,
} from '@api/lib/payrollProcessing/types';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';
import {ExtractedFile, Mapping, TargetFieldInfo} from '@client/pages/payroll/types';
import {getTransformationColumnsFromMapping} from '@client/pages/payroll/utils/transformationUtils';

export type LocalValidationErrorType = 'general' | 'mapping' | 'anchor' | 'unmapped';

export type LocalValidationError = {
  message: string;
  targetField?: string;
  anchorFieldIdx?: number;
  type: LocalValidationErrorType;
};

export function validateConfiguration(
  targetFields: TargetFieldInfo[],
  mappings: PayrollFileColumnMapping[],
  extracted: ExtractedFile[],
  anchorFields: PayrollAnchorField[],
  ignoredColumns: string[] = [],
): {
  isValid: boolean;
  errors: string[];
  structuredErrors: LocalValidationError[];
  unmappedColumns: string[];
  usedColumns: string[];
} {
  const structuredErrors: LocalValidationError[] = [];

  // Check for required fields that are not mapped
  const requiredFields = targetFields.filter((tf) => tf.isRequired);
  const mappedTargetFields = mappings.map((m) => m.targetField);

  const missingRequired = requiredFields.filter(
    (rf) => !mappedTargetFields.includes(rf.field as unknown as PayrollTargetField),
  );
  if (missingRequired.length > 0) {
    missingRequired.forEach((f) => {
      structuredErrors.push({
        message: `Missing required field mapping: ${f.label}`,
        targetField: f.field,
        type: 'mapping',
      });
    });
  }

  // Check for duplicate target field mappings
  const targetFieldCounts = mappings.reduce(
    (acc, mapping) => {
      acc[mapping.targetField] = (acc[mapping.targetField] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const duplicates = Object.entries(targetFieldCounts)
    .filter(([_, count]) => count > 1)
    .map(([field]) => ({
      field,
      label: targetFields.find((tf) => tf.field === field)?.label || field,
    }));

  if (duplicates.length > 0) {
    duplicates.forEach((dup) => {
      structuredErrors.push({
        message: `Duplicate mapping for field: ${dup.label}`,
        targetField: dup.field,
        type: 'mapping',
      });
    });
  }

  // Check for mappings with invalid column transformations
  const invalidMappings = mappings.filter((mapping) => {
    const ct = mapping.columnTransformation;
    if (ct.type === 'NONE' && (!ct.column || !ct.column.fileId || !ct.column.name)) {
      return true;
    }
    if (
      [
        'COLUMN_ADDITION',
        'COLUMN_AVERAGE',
        'COLUMN_MAXIMUM',
        'COLUMN_MINIMUM',
        'COLUMN_TEXT_CONCATENATION',
      ].includes(ct.type)
    ) {
      const multiColumnTransform = ct as {
        columns?: PayrollFileColumnTransformationColumn[];
      };
      return !multiColumnTransform.columns || multiColumnTransform.columns.length === 0;
    }
    return false;
  });

  if (invalidMappings.length > 0) {
    invalidMappings.forEach((m) => {
      const label = targetFields.find((tf) => tf.field === m.targetField)?.label || m.targetField;
      structuredErrors.push({
        message: `Invalid column configuration for: ${label}`,
        targetField: m.targetField,
        type: 'mapping',
      });
    });
  }

  // Check for value transformations with invalid configurations
  const invalidValueTransformations = mappings.filter((mapping) => {
    return mapping.valueTransformations?.some((vt) => {
      if (vt.type === 'VALUE_MAPPING' && (!vt.mappings || vt.mappings.length === 0)) {
        return true;
      }
      return false;
    });
  });

  if (invalidValueTransformations.length > 0) {
    invalidValueTransformations.forEach((m) => {
      const label = targetFields.find((tf) => tf.field === m.targetField)?.label || m.targetField;
      structuredErrors.push({
        message: `Invalid value transformation for: ${label}`,
        targetField: m.targetField,
        type: 'mapping',
      });
    });
  }

  // Check for anchor fields when multiple files are present
  if (extracted.length > 1 && anchorFields.length === 0) {
    structuredErrors.push({
      message:
        'Multiple files detected but no file linking configuration found. Please configure how to match records across files.',
      type: 'anchor',
    });
  }

  if (extracted.length > 1 && anchorFields.length > 0) {
    const fileIds = extracted.map((f) => f.file.fileId);

    anchorFields.forEach((anchor, idx) => {
      if (anchor.type === 'RAW_COLUMNS') {
        // Check that anchor includes all files
        const filesInAnchor = new Set(anchor.columns?.map((c) => c.fileId) || []);
        const missingFiles = fileIds.filter((fid) => !filesInAnchor.has(fid));

        if (missingFiles.length > 0) {
          structuredErrors.push({
            message: `File anchor "${anchor.label || 'Unnamed'}" is missing columns from files: ${missingFiles.join(', ')}. Please select a column from each file.`,
            anchorFieldIdx: idx,
            type: 'anchor',
          });
        }
      }
    });
  }

  // Check for unmapped columns
  const unmappedColumns: string[] = [];
  const usedColumns = new Set<string>();

  if (extracted.length > 0) {
    // Get all columns used in mappings
    for (const mapping of mappings) {
      const columns = getTransformationColumnsFromMapping(mapping);
      columns.forEach((col) => {
        usedColumns.add(`${col.fileId}:${col.name}`);
      });
    }

    // Get all columns used in anchor fields
    for (const anchor of anchorFields) {
      if (anchor.type === 'RAW_COLUMNS') {
        anchor.columns?.forEach((col) => {
          usedColumns.add(`${col.fileId}:${col.name}`);
        });
      }
    }

    // Check all extracted columns
    for (const file of extracted) {
      for (const col of file.columns) {
        const colId = `${file.file.fileId}:${col.name}`;
        if (!usedColumns.has(colId) && !ignoredColumns.includes(colId)) {
          unmappedColumns.push(colId);
        }
      }
    }

    if (unmappedColumns.length > 0) {
      structuredErrors.push({
        message: `${unmappedColumns.length} column${unmappedColumns.length !== 1 ? 's' : ''} from your file${extracted.length !== 1 ? 's' : ''} ${unmappedColumns.length !== 1 ? 'are' : 'is'} not mapped or ignored. Please map ${unmappedColumns.length !== 1 ? 'them' : 'it'} to a field or explicitly ignore ${unmappedColumns.length !== 1 ? 'them' : 'it'}.`,
        type: 'unmapped',
      });
    }
  }

  const errors: string[] = [];

  if (missingRequired.length > 0) {
    errors.push(
      `Missing required field mappings: ${missingRequired.map((f) => f.label).join(', ')}`,
    );
  }
  if (duplicates.length > 0) {
    errors.push(`Duplicate field mappings found: ${duplicates.map((d) => d.label).join(', ')}`);
  }
  if (invalidMappings.length > 0) {
    const labels = invalidMappings.map(
      (m) => targetFields.find((tf) => tf.field === m.targetField)?.label || m.targetField,
    );
    errors.push(`Invalid column configurations for: ${labels.join(', ')}`);
  }
  if (invalidValueTransformations.length > 0) {
    const labels = invalidValueTransformations.map(
      (m) => targetFields.find((tf) => tf.field === m.targetField)?.label || m.targetField,
    );
    errors.push(`Invalid value transformations for: ${labels.join(', ')}`);
  }

  structuredErrors
    .filter((e) => e.type === 'anchor')
    .forEach((e) => {
      if (!errors.includes(e.message)) {
        errors.push(e.message);
      }
    });

  structuredErrors
    .filter((e) => e.type === 'unmapped')
    .forEach((e) => {
      if (!errors.includes(e.message)) {
        errors.push(e.message);
      }
    });

  return {
    isValid: errors.length === 0,
    errors,
    structuredErrors,
    unmappedColumns,
    usedColumns: Array.from(usedColumns),
  };
}

export function validateAnchorFields(
  anchorFields: PayrollAnchorField[],
  extracted: ExtractedFile[],
): {isValid: boolean; errors: string[]} {
  const errors: string[] = [];

  if (anchorFields.length === 0) {
    // handled by overall config validation
    return {isValid: true, errors};
  }

  const fileIds = extracted.map((f) => f.file.fileId);
  const fileIdToFile = new Map(extracted.map((f) => [f.file.fileId, f]));

  const normalizedLabels = anchorFields.map((a) => (a.label || '').trim().toLowerCase());
  const labelCounts: Record<string, number> = {};
  normalizedLabels.forEach((nl) => {
    if (!nl) return;
    labelCounts[nl] = (labelCounts[nl] || 0) + 1;
  });
  const duplicateLabels = Object.entries(labelCounts)
    .filter(([_, count]) => count > 1)
    .map(([label]) => label);
  if (duplicateLabels.length > 0) {
    errors.push(
      `Duplicate file link names found: ${duplicateLabels.map((l) => `"${l}"`).join(', ')}.`,
    );
  }

  anchorFields.forEach((anchor, idx) => {
    const displayName = anchor.label?.trim() || `File Link #${idx + 1}`;

    if (!anchor.label || anchor.label.trim() === '') {
      errors.push(
        `File link #${idx + 1} is missing a link name. Enter a descriptive name (e.g., "Employee ID").`,
      );
    }

    if (anchor.type === 'RAW_COLUMNS') {
      const selectedByFile = new Map<string, {fileId?: string; name?: string; index?: number}>();
      (anchor.columns || []).forEach((c) => {
        if (c.fileId) {
          selectedByFile.set(c.fileId, c);
        }
      });

      const missingFiles = fileIds.filter((fid) => !selectedByFile.has(fid));
      if (missingFiles.length > 0) {
        errors.push(
          `File link "${displayName}" is missing a selected column from: ${missingFiles.join(
            ', ',
          )}. Please select a column from each file.`,
        );
      }

      for (const [fileId, selected] of selectedByFile.entries()) {
        const file = fileIdToFile.get(fileId);
        if (!file) {
          errors.push(
            `File link "${displayName}" references an unknown file "${fileId}". Please re-select a column.`,
          );
          continue;
        }
        const matchByIndex = file.columns.find((c) => c.index === selected.index);
        if (!matchByIndex) {
          const fileLabel = `${file.file.fileName} (${file.file.fileId})`;
          errors.push(
            `File link "${displayName}" has an invalid column selection for ${fileLabel}. Please re-select a valid column.`,
          );
        }
      }
    }
  });

  return {isValid: errors.length === 0, errors};
}

export function getConfigurationDisclaimer(field: PayrollTargetField): string {
  switch (field) {
    case 'FEDERAL_2020_W4_OR_LATER_USED_YN': {
      return 'NOTE: If an effective date is entered in this field, this field will resolve to Yes or No depending on whether the date is before or after 1/1/2020.';
    }
    default: {
      return '';
    }
  }
}

export function validateTransformationColumns(mapping: Mapping | undefined): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!mapping) {
    return {isValid: false, errors: ['No transformation configured.']};
  }

  function validateColumn(
    column: PayrollFileColumnTransformationColumn,
    rowIdx: number | null,
    contextPrefix?: string,
  ) {
    const prefix = contextPrefix ?? (rowIdx !== null ? `Column ${rowIdx + 1}: ` : '');
    if (!column.name || String(column.name).trim().length === 0) {
      errors.push(`${prefix}Selected column name is required.`);
    }
    if (!column.fileId || String(column.fileId).trim().length === 0) {
      errors.push(`${prefix}Selected column fileId is required.`);
    }
    // Pivot columns can have negative indices (they use -1000 and below to avoid conflicts)
    // Regular columns must have index >= 0
    const isPivotColumn = Boolean(column.pivotKeyValue);
    if (typeof column.index !== 'number' || (!isPivotColumn && column.index < 0)) {
      errors.push(`${prefix}Selected column index must be 0 or greater.`);
    }

    (column.fallbackColumns || []).forEach((fallbackCol, fallbackIdx) => {
      validateColumn(
        fallbackCol,
        null,
        `${rowIdx !== null ? `Column ${rowIdx + 1}` : 'Column'} fallback ${fallbackIdx + 1}: `,
      );
    });
  }

  if (!mapping.columnTransformation) {
    errors.push('Column transformation is required.');
  } else {
    switch (mapping.columnTransformation.type) {
      case 'NONE': {
        const column = mapping.columnTransformation.column;
        if (!column) {
          errors.push('Please select a source column.');
        } else {
          validateColumn(column, null);
        }
        break;
      }
      case 'COLUMN_FORMULA': {
        const cols = mapping.columnTransformation.columns;
        const formula = mapping.columnTransformation.formula;
        if (!cols || cols.length < 1) {
          errors.push('Formula transformation requires at least one column.');
        }
        if (formula === undefined || String(formula).trim().length === 0) {
          errors.push('Formula is required.');
        }
        if (formula && String(formula).length > 500) {
          errors.push('Formula is too long (max 500 characters).');
        }
        if (cols) {
          cols.forEach((col, idx) => {
            validateColumn(col, idx);
          });
        }
        break;
      }
      case 'COLUMN_FALLBACK':
      case 'COLUMN_TEXT_CONCATENATION':
      case 'COLUMN_ADDITION':
      case 'COLUMN_SUBTRACTION':
      case 'COLUMN_MULTIPLICATION':
      case 'COLUMN_DIVISION':
      case 'COLUMN_AVERAGE':
      case 'COLUMN_MAXIMUM':
      case 'COLUMN_MINIMUM': {
        const cols = mapping.columnTransformation.columns;
        const minColumns = 2;
        if (!cols || cols.length < minColumns) {
          errors.push('Please select at least two source columns.');
        }
        if (cols) {
          cols.forEach((col, idx) => {
            validateColumn(col, idx);
          });
        }
        break;
      }
      case 'PIVOT_COLUMN': {
        const pivotCol = mapping.columnTransformation.pivotColumn;
        if (!pivotCol.fileId) {
          errors.push('Pivot column must reference a file.');
        }
        if (!pivotCol.name) {
          errors.push('Pivot column must have a name.');
        }
        break;
      }
      case 'STATIC_VALUE': {
        const val = mapping.columnTransformation.staticValue;
        if (!val || String(val).trim().length === 0) {
          errors.push('Static value is required.');
        }
        break;
      }
      default: {
        const _exhaustiveCheck: never = mapping.columnTransformation;
        break;
      }
    }
  }

  if (mapping.valueTransformations.length > 10) {
    errors.push('Too many value transformations (max 10).');
  }
  mapping.valueTransformations.forEach((valueTransformation, idx) => {
    switch (valueTransformation.type) {
      case 'VALUE_MAPPING': {
        const mappings = valueTransformation.mappings;
        if (!mappings || mappings.length === 0) {
          errors.push(`Transformation #${idx + 1}: At least one mapping is required.`);
          break;
        }
        mappings.forEach((m, mi) => {
          const from = String(m?.from ?? '');
          const to = String(m?.to ?? '');
          if (from.trim().length === 0) {
            errors.push(`Transformation #${idx + 1}, row ${mi + 1}: "From" is required.`);
          }
          if (from.length > 300) {
            errors.push(`Transformation #${idx + 1}, row ${mi + 1}: "From" exceeds 300 chars.`);
          }
          if (to.length > 300) {
            errors.push(`Transformation #${idx + 1}, row ${mi + 1}: "To" exceeds 300 chars.`);
          }
        });
        break;
      }
      case 'DEFAULT_IF_EMPTY': {
        const defaultValue = String(valueTransformation.defaultValue ?? '');
        if (defaultValue.trim().length === 0) {
          errors.push(`Transformation #${idx + 1}: Default value is required.`);
        }
        if (defaultValue.length > 300) {
          errors.push(`Transformation #${idx + 1}: Default value exceeds 300 chars.`);
        }
        break;
      }
      case 'DEFAULT_UNLESS_VALUE': {
        const unlessValue = valueTransformation.unlessValue ?? '';
        const defaultValue = String(valueTransformation.defaultValue ?? '');
        if (unlessValue.trim().length === 0) {
          errors.push(`Transformation #${idx + 1}: "Unless value" is required.`);
        }
        if (unlessValue.length > 300) {
          errors.push(`Transformation #${idx + 1}: "Unless value" exceeds 300 chars.`);
        }
        if (defaultValue.trim().length === 0) {
          errors.push(`Transformation #${idx + 1}: Default value is required.`);
        }
        if (defaultValue.length > 300) {
          errors.push(`Transformation #${idx + 1}: Default value exceeds 300 chars.`);
        }
        break;
      }
      case 'PAD_STRING': {
        const padChar = valueTransformation.padCharacter ?? '';
        const targetLen = valueTransformation.targetLength;
        if (padChar.length !== 1) {
          errors.push(`Transformation #${idx + 1}: Pad character must be exactly one character.`);
        }
        if (typeof targetLen !== 'number' || !Number.isInteger(targetLen) || targetLen < 1) {
          errors.push(`Transformation #${idx + 1}: Target length must be a positive whole number.`);
        }
        if (typeof targetLen === 'number' && targetLen > 100) {
          errors.push(`Transformation #${idx + 1}: Target length cannot exceed 100.`);
        }
        if (!valueTransformation.padPosition) {
          errors.push(`Transformation #${idx + 1}: Pad position (start or end) is required.`);
        }
        break;
      }
      case 'SUBSTRING': {
        const startIdx = valueTransformation.startIndex;
        const len = valueTransformation.length;
        if (typeof startIdx !== 'number' || !Number.isInteger(startIdx)) {
          errors.push(`Transformation #${idx + 1}: Start position must be a whole number.`);
        }
        // startIdx must be in range from -1000 to 1000, except 0
        if (startIdx === 0) {
          errors.push(
            `Transformation #${idx + 1}: Start position cannot be 0. Use 1 for first character, -1 for last.`,
          );
        }
        if (startIdx < -1000 || startIdx > 1000) {
          errors.push(`Transformation #${idx + 1}: Start position must be between -1000 and 1000.`);
        }
        if (len !== undefined) {
          if (typeof len !== 'number' || !Number.isInteger(len) || len < 1) {
            errors.push(`Transformation #${idx + 1}: Length must be a positive whole number.`);
          }
          if (len > 1000) {
            errors.push(`Transformation #${idx + 1}: Length cannot exceed 1000.`);
          }
        }
        break;
      }
      case 'SPLIT_AND_EXTRACT': {
        const delimiter = valueTransformation.delimiter ?? '';
        const partIdx = valueTransformation.partIndex;
        if (delimiter.length === 0) {
          errors.push(`Transformation #${idx + 1}: Delimiter is required.`);
        }
        if (delimiter.length > 10) {
          errors.push(`Transformation #${idx + 1}: Delimiter cannot exceed 10 characters.`);
        }
        if (typeof partIdx !== 'number' || !Number.isInteger(partIdx)) {
          errors.push(`Transformation #${idx + 1}: Part index must be a whole number.`);
        }
        if (partIdx === 0) {
          errors.push(
            `Transformation #${idx + 1}: Part index cannot be 0. Use 1 for first part, -1 for last.`,
          );
        }
        break;
      }
      case 'NONE':
      case 'TRIM':
      case 'TO_UPPERCASE':
      case 'TO_LOWERCASE':
      case 'REMOVE_NON_NUMERIC':
      case 'REMOVE_NON_ALPHABETIC':
      case 'PARSE_NUMBER':
      case 'CONVERT_BLANK_TO_EMPTY': {
        break;
      }
      default: {
        const _exhaustiveCheck: never = valueTransformation;
        break;
      }
    }
  });

  return {isValid: errors.length === 0, errors};
}
